import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const HastingsSunrise = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:About Hastings-Sunrise Paragraph">
          Hastings-Sunrise in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">BC</LinkText>, is a
          culturally diverse and community-oriented neighborhood, known for its
          vibrant mix of residential areas and a bustling commercial district
          along{' '}
          <LinkText href="https://en.wikipedia.org/wiki/Hastings_Street_(Vancouver)">
            East Hastings Street
          </LinkText>
          . This area offers a variety of international restaurants, unique
          local shops, and regular community events, underscored by convenient
          public transport options, including several bus routes and accessible{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>{' '}
          stations. While it doesn’t sit near the seawall or major hiking
          trails, Hastings-Sunrise is close to natural attractions like{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=75">
            New Brighton Park
          </LinkText>{' '}
          and the{' '}
          <LinkText href="https://www.pne.ca/">
            Pacific National Exhibition (PNE)
          </LinkText>
          , offering residents a balance of urban living, cultural richness, and
          outdoor recreational opportunities.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$1,800 - $2,900',
          bed2: '$2,600 - $3,500',
          bed3: '$3,700 - $4,900'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Apartment Hunting Hastings-Sunrise Paragraph 1">
            Finding an apartment for rent in Hastings-Sunrise, Vancouver, BC
            requires careful planning and consideration. Start by determining
            your criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Hastings-Sunrise lifestyle by exploring
            your new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Apartment Hunting Hastings-Sunrise Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Apartments for rent in Hastings-Sunrise Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Hastings-Sunrise
              </LinkText>{' '}
              provide a practical and convenient lifestyle, perfect for
              individuals or small families. These units often come with a
              variety of floor plans and modern amenities, offering multiple
              bedrooms and bathrooms and a low-maintenance way of living.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Houses for rent in Hastings-Sunrise Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Hastings-Sunrise
              </LinkText>{' '}
              offer more space and a sense of home ownership. With their own
              yards and outdoor spaces, these homes are ideal for those who love
              gardening or outdoor activities. They offer more room for
              customization and personalization, allowing residents to create a
              space that truly feels like their own.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Townhouses for rent in Hastings-Sunrise Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Hastings-Sunrise
              </LinkText>{' '}
              strike a balance between the privacy of a house and the community
              aspect of apartment living. These multi-level homes often include
              multiple bedrooms and bathrooms, ensuite facilities, and private
              outdoor areas like balconies or patios. With independent entrances
              and sometimes attached garages, townhouses are a popular choice
              for families or individuals seeking more space and amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Basements for rent in Hastings-Sunrise Paragraph">
              Basements for rent in Hastings-Sunrise present an affordable
              housing option. These lower-level units, ranging from cozy studios
              to more spacious suites, usually have their own separate
              entrances. They provide a budget-friendly choice for individuals
              or couples, offering the benefits of living in a desirable area
              without the high costs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Condos for rent Hastings-Sunrise Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Hastings-Sunrise
              </LinkText>{' '}
              cater to those seeking a more modern and luxurious living
              experience. Typically found in high-rise buildings, these condos
              offer amenities like fitness centers, swimming pools, and rooftop
              terraces. With their stylish finishes and open-concept designs,
              condos in this neighborhood are ideal for professionals and
              individuals who prioritize convenience and quality amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Rooms for rent in Hastings-Sunrise Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Hastings-Sunrise
              </LinkText>{' '}
              are suitable for individuals looking for a shared living
              arrangement. This affordable option often involves renting a spare
              room in a house or an apartment and is a great way to meet new
              people and engage in community living. It’s an economical and
              social way to live in one of Vancouver’s vibrant neighborhoods.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Short term rentals in Hastings-Sunrise Paragraph">
              Hastings-Sunrise, a neighborhood in Vancouver, is known for its
              family-friendly atmosphere and community focus. Situated in the
              northeastern corner of the city, it offers a mix of residential
              areas and local businesses, with views of the North Shore
              mountains and proximity to both downtown and natural parks. While
              not a major center for employment or academic institutions,
              Hastings-Sunrise is appreciated for its quieter, more residential
              character and local amenities, including diverse eateries and
              shops along East Hastings.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
              here attract those who prefer a more relaxed, community-oriented
              environment while still being close to the city’s core. This
              neighborhood, combining a serene residential setting with urban
              convenience, is{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                appealing for short-term stays
              </LinkText>
              , especially for those looking to enjoy a quieter part of
              Vancouver
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Exploring Hastings-Sunrise Paragraph">
          Hastings-Sunrise, located in Vancouver, British Columbia, is an
          enchanting neighborhood that begs to be explored due to its rich
          history, cultural diversity, and breathtaking natural surroundings.
          Nestled on the eastern side of downtown Vancouver, this area is
          renowned for its charming residential architecture and welcoming
          community. A visit to Hastings-Sunrise would not be complete without
          experiencing the tranquility of{' '}
          <LinkText href="https://vancouver.ca/parks-recreation-culture/hastings-park.aspx">
            Hastings Park
          </LinkText>
          , a local green oasis that offers respite from the city’s fast-paced
          lifestyle. Moreover, art enthusiasts will be delighted by the
          neighborhood’s thriving arts scene, which showcases the talents of
          local artists through a myriad of galleries and studios. Whether
          you’re a history aficionado or simply seeking an authentic cultural
          experience, Hastings-Sunrise is an absolute must-visit destination in
          Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Things To Do Hastings-Sunrise Paragraph">
          Hastings-Sunrise, a vibrant neighborhood in Vancouver, BC, offers a
          plethora of exciting activities and attractions for both locals and
          tourists. Nature lovers can immerse themselves in the stunning beauty
          of Hastings Park, where they can explore picturesque trails for hiking
          and biking adventures. Art enthusiasts will find inspiration in the
          neighborhood’s numerous galleries and studios, showcasing a diverse
          range of contemporary and historical artworks. Foodies can indulge in
          a culinary exploration by visiting the local eateries and food
          markets, where they can savor delicious dishes made with fresh,
          locally sourced ingredients. Shopaholics can wander through the trendy
          boutiques and artisanal shops that line the streets of
          Hastings-Sunrise, offering a unique shopping experience. Additionally,
          the neighborhood’s rich history and cultural heritage come alive
          through various landmarks and heritage sites, providing visitors with
          a glimpse into the past. With its blend of natural beauty, artistic
          expression, culinary delights, and cultural experiences,
          Hastings-Sunrise is a must-visit destination that offers something for
          everyone.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Restaurants Hastings-Sunrise Paragraph">
        <Paragraph>
          Hastings-Sunrise is a paradise for those who love exploring new
          restaurants. This lively district is home to a diverse culinary
          landscape that promises to entice every taste bud. Whether you’re in
          the mood for international fare or local delicacies, Hastings-Sunrise
          offers an array of exceptional dining establishments that are worthy
          of your attention.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="http://www.redwagoncafe.com/">
            Red Wagon Café
          </LinkText>{' '}
          - Famous for its all-day breakfast and brunch menu, Red Wagon Café is
          a local favorite, especially known for its pulled pork pancakes and
          other hearty dishes.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.tacofino.com/location/hastings-commissary">
            Tacofino Commissary
          </LinkText>{' '}
          - Part of the popular Tacofino chain known for its unique and
          flavorful tacos and burritos, this location offers a laid-back
          atmosphere with a variety of Mexican-inspired dishes and drinks.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://m.yelp.com/biz/bao-chau-vietnamese-restaurant-vancouver">
            Bao Chau Vietnamese
          </LinkText>{' '}
          - A longstanding establishment in the area, Bao Chau is praised for
          its authentic Vietnamese pho, spring rolls, and other traditional
          dishes.
        </Paragraph>
        <Paragraph>
          4) <LinkText href="https://roundel.cafe/">Roundel Café</LinkText> - A
          cozy and welcoming spot, Roundel Café serves up comfort food with a
          focus on fresh, locally-sourced ingredients. It’s a great place for
          breakfast, lunch, or a casual dinner.
        </Paragraph>
        <Paragraph>
          5) <LinkText href="https://www.yamacafe.ca/">Yama Café</LinkText> -
          This quaint Japanese café offers a selection of homemade Japanese
          dishes and sweets, known for its relaxed ambiance and friendly
          service.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Transportation Hastings-Sunrise Paragraph">
          Hastings-Sunrise in Vancouver, BC, offers convenient transportation
          options, primarily served by an{' '}
          <LinkText href="https://www.translink.ca/">
            extensive bus network
          </LinkText>{' '}
          that connects the neighborhood to the rest of the city and nearby{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>{' '}
          stations for broader access. While it’s not a cycling hub on the level
          of some other Vancouver neighborhoods, it does support bike travel
          with friendly routes. The area is also quite walkable, especially
          around key commercial and retail zones, allowing easy access to local
          amenities. For drivers, street parking is available, though more
          variable in residential areas, with more structured parking options in
          commercial districts. This blend of transportation modes makes
          Hastings-Sunrise an accessible and practical choice for residents and
          visitors.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.hastings-sunrise-vancouver:Where To Stay Hastings-Sunrise Paragraph">
          In Hastings-Sunrise, Vancouver, BC, while there are no major hotels
          within the neighborhood itself, nearby options include the{' '}
          <LinkText href="https://www.atriuminn.ca/">
            Atrium Inn Vancouver
          </LinkText>
          , offering comfortable accommodations close to downtown, and the{' '}
          <LinkText href="https://www.accentinns.com/locations/burnaby-hotel/">
            Accent Inns Burnaby
          </LinkText>
          , a bit further but affordable and convenient. For a more upscale
          stay, the{' '}
          <LinkText href="https://www.pinnaclepierhotel.com/">
            Pinnacle Hotel at the Pier
          </LinkText>{' '}
          in{' '}
          <LinkText href="https://liv.rent/rental-listings/city/north-vancouver">
            North Vancouver
          </LinkText>{' '}
          is an option, albeit a bit of a drive away. Additionally, within
          Hastings-Sunrise, visitors can find a variety of short-term rental
          properties on platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>, ranging
          from rooms in shared homes to entire houses, providing a more
          authentic local experience. These accommodation choices allow visitors
          to balance proximity, cost, and the desire to experience the unique
          character of the area.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default HastingsSunrise
